import React from 'react';
import { Invoice } from '../../types';
import { agreedCashDiscount, matchesAgreedCashDiscount } from '../../services/cashDiscount';
import {
    checkRemainder,
    paymentRemainder,
    REMAINDER_LABEL,
    RemainderKind,
    remainderPercent,
} from '../../services/paymentRemainder';
import { formatCents } from '../../services/money';

/**
 * Was mit dem Rest passiert, wenn weniger gezahlt wird als offen ist — im
 * Zahlungsbereich, im Bezahlt-Dialog und im Kontoauszugsimport dieselbe Wahl.
 *
 * Drei Moeglichkeiten: offen lassen (Teilzahlung), Skonto oder
 * Sicherheitseinbehalt des Kunden. Nie vorbelegt — jede Wahl ist eine
 * Entscheidung. Passt der Rest genau zum vereinbarten Skonto und kam die
 * Zahlung in der Frist, steht das dabei.
 *
 * Bei mehreren Steuersaetzen gibt es die Wahl nicht: die Aufteilung des
 * Rests in Netto und Umsatzsteuer liesse sich nicht rechnen. Dort wird der
 * Rest als eigene Zahlung mit der Zahlungsart „Skonto" oder
 * „Sicherheitseinbehalt" samt Aufteilung erfasst.
 */

interface Props {
    /** Eindeutig je Maske/Zeile — die Optionsfelder bilden eine Gruppe. */
    name: string;
    invoice: Invoice;
    /** Zahlbetrag in Cent; null, solange die Eingabe nicht lesbar ist. */
    paymentCents: number | null;
    valueDate: string;
    value: RemainderKind | null;
    onChange: (value: RemainderKind | null) => void;
    disabled?: boolean;
    /** Kleine Schrift fuer die Tabelle des Kontoauszugsimports. */
    compact?: boolean;
}

const KINDS: RemainderKind[] = ['CASH_DISCOUNT', 'CUSTOMER_RETENTION'];

export const RemainderOption: React.FC<Props> = ({
    name,
    invoice,
    paymentCents,
    valueDate,
    value,
    onChange,
    disabled,
    compact,
}) => {
    const remainder = paymentCents == null ? 0 : paymentRemainder(invoice, paymentCents);
    if (remainder <= 0 || paymentCents == null) return null;

    const size = compact ? 'text-[11px]' : 'text-xs';
    const multiRate = invoice.totals.taxGroups.filter((g) => g.taxableNetCents !== 0).length > 1;
    if (multiRate) {
        return compact ? null : (
            <p className={`${size} text-slate-500`}>
                Rest {formatCents(remainder)}: bei mehreren Steuersätzen bitte als eigene Zahlung
                mit der Zahlungsart „Skonto“ oder „Sicherheitseinbehalt“ und Aufteilung erfassen.
            </p>
        );
    }

    const percent = remainderPercent(invoice, remainder).toLocaleString('de-DE', {
        maximumFractionDigits: 2,
    });
    const agreed = agreedCashDiscount(invoice);
    const skontoFits = !!agreed && matchesAgreedCashDiscount(invoice, remainder, valueDate);
    const messages = value ? checkRemainder(invoice, value, remainder, valueDate, paymentCents) : [];
    const radio = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';

    return (
        <fieldset className={`${size} ${compact ? 'mt-1' : ''}`} disabled={disabled}>
            <legend className="text-slate-700">
                Rest {formatCents(remainder)} ({percent} %):
            </legend>
            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-slate-700">
                <label className="flex items-center gap-1">
                    <input
                        type="radio"
                        name={name}
                        checked={value === null}
                        onChange={() => onChange(null)}
                        className={radio}
                    />
                    offen lassen
                </label>
                {KINDS.map((kind) => (
                    <label key={kind} className="flex items-center gap-1">
                        <input
                            type="radio"
                            name={name}
                            checked={value === kind}
                            onChange={() => onChange(kind)}
                            className={radio}
                        />
                        als {REMAINDER_LABEL[kind]}
                    </label>
                ))}
            </div>
            {skontoFits && value !== 'CUSTOMER_RETENTION' && (
                <span className="block text-emerald-700">
                    entspricht dem vereinbarten Skonto ({agreed.percent} % in {agreed.days} Tagen)
                </span>
            )}
            {messages.map((m) => (
                <span
                    key={m.code}
                    className={`mt-0.5 block ${m.level === 'error' ? 'text-red-600' : 'text-amber-700'}`}
                >
                    {m.message}
                </span>
            ))}
        </fieldset>
    );
};
